import type { Trip } from '@viaja/contracts';
import { Button, EmptyState, Icon } from '@viaja/design-system';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { money, tripPeriod } from '../../lib/format';
import { useTrips } from '../../lib/queries';
import { MainScreen } from '../../ui/AppShell';
import { LoadError, Loading } from '../../ui/feedback';
import { TripFormSheet } from './TripFormSheet';

export const TRIP_STATUS_LABEL: Record<Trip['status'], string> = {
  planejando: 'Planejando',
  comprada: 'Passagem comprada',
  arquivada: 'Arquivada',
};

export function TripsPage() {
  const trips = useTrips();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  return (
    <MainScreen>
      {trips.isPending ? (
        <Loading label="Buscando suas viagens…" />
      ) : trips.error ? (
        <LoadError message={trips.error.message} onRetry={() => trips.refetch()} />
      ) : trips.data.length === 0 ? (
        <EmptyState
          pose="viagem"
          title="Nenhuma viagem planejada"
          body="Junte as rotas de uma mesma viagem e acompanhe o total num lugar só."
          actionLabel="Planejar uma viagem"
          onAction={() => setCreating(true)}
        />
      ) : (
        <>
          <div className="app-head">
            <h1 className="display-title">Suas viagens</h1>
          </div>
          <ul className="app-list">
            {trips.data.map((t) => (
              <li key={t.id}>
                <Link to={`/viagens/${t.id}`} className="app-cardlink">
                  <TripCard trip={t} />
                </Link>
              </li>
            ))}
          </ul>
          <div className="app-actionbar">
            <Button icon="mais" block onClick={() => setCreating(true)}>
              Planejar uma viagem
            </Button>
          </div>
        </>
      )}
      <TripFormSheet
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(t) => navigate(`/viagens/${t.id}`)}
      />
    </MainScreen>
  );
}

/** Cartão de viagem com as classes de card do DS (vc-card, vc-route). */
export function TripCard({ trip }: { trip: Trip }) {
  return (
    <article className="vc-card vc-route">
      <header className="vc-route-head">
        <span className="display-section">{trip.name}</span>
        <Icon name="mala" size={20} />
      </header>
      <p className="small vc-muted">{tripPeriod(trip.startDate, trip.endDate)}</p>
      <div className="vc-route-foot">
        <span className="small">
          {trip.routeCount === 0
            ? 'Nenhuma rota ainda'
            : trip.routeCount === 1
              ? '1 rota'
              : `${trip.routeCount} rotas`}
          {trip.bestTotal ? (
            <>
              {' · '}
              <strong className="vc-num">{money(trip.bestTotal)}</strong> hoje
            </>
          ) : null}
        </span>
        <span className="caption vc-muted">{TRIP_STATUS_LABEL[trip.status]}</span>
      </div>
    </article>
  );
}
